import { memo, useRef, useCallback } from 'react'
import { useSortable } from '@dnd-kit/sortable'
import { CSS } from '@dnd-kit/utilities'
import { Badge, Box, Button, Checkbox, Flex, Spacer, Text } from '@chakra-ui/react'
import { MdDragHandle } from 'react-icons/md'
import { IoIosRemoveCircleOutline } from 'react-icons/io'
import { BiSolidHide, BiShow } from 'react-icons/bi'
import TextFilter from '@/components/Table/components/Filter/TextFilter'
import { FilterType } from '@/components/Table/components/Filter/TableFilter'
import { DateFilter, MultipleSelectFilter, RangeFilter, SelectFilter } from '@/components/Table/components/Filter'
import ReactSelect, { createFilter } from 'react-select'
import UncontrolledMultiSelect from '@/components/UncontrolledForm/UncontrolledMultiSelect'

const TableProfileColumnItem = ({ column, handleUpdateColumn }) => {
  const { id, label, show, filterValue, filterType, filterOptions } = column

  const { attributes, listeners, setNodeRef, transform, transition } = useSortable({ id: id })

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  }

  const handleShowColumn = useCallback(() => {
    handleUpdateColumn(id, { ...column, show: true })
  }, [id])

  const handleHideColumn = useCallback(() => {
    handleUpdateColumn(id, { ...column, show: false })
  }, [id])

  const handleUpdateColumnFilter = useCallback(
    (newFilterValue) => {
      handleUpdateColumn(id, { ...column, filterValue: newFilterValue })
    },
    [id]
  )

  return (
    <Flex
      ref={setNodeRef}
      style={style}
      alignItems={'center'}
      gap="2"
      border="1px solid #d8dbe0"
      borderRadius={10}
      pl="1"
    >
      <Box color="gray.400" {...attributes} {...listeners} _hover={{ cursor: 'grab' }}>
        <MdDragHandle size={25} />
      </Box>
      <ColumnItem
        {...column}
        handleUpdateColumnFilter={handleUpdateColumnFilter}
        handleShowColumn={handleShowColumn}
        handleHideColumn={handleHideColumn}
      />
    </Flex>
  )
}

const ColumnItem = memo(
  ({
    show,
    label,
    filterType,
    filterOptions,
    filterValue,
    handleUpdateColumnFilter,
    handleShowColumn,
    handleHideColumn,
  }) => {
    return (
      <Flex alignItems={'center'} w="100%" gap="2">
        {show ? (
          <Box _hover={{ cursor: 'pointer' }} justifyContent={'center'} w="24px">
            <BiSolidHide
              size={24}
              onClick={(e) => {
                e.stopPropagation()
                handleHideColumn()
              }}
            />
          </Box>
        ) : (
          <Box _hover={{ cursor: 'pointer' }} justifyContent={'center'} w="24px">
            <BiShow
              size={24}
              onClick={(e) => {
                e.stopPropagation()
                handleShowColumn()
              }}
            />
          </Box>
        )}
        <Flex w="500px" justifyContent={'space-between'}>
          <Text>{label}</Text>
        </Flex>
        <Spacer />

        <Flex w="70%">
          {!show ? (
            <Badge variant={'solid'} colorScheme="gray" my="3">
              hidden
            </Badge>
          ) : (
            <>
              {filterType === FilterType.TEXT && (
                <TextFilter value={filterValue} direction={'row'} setValue={handleUpdateColumnFilter} />
              )}
              {filterType === FilterType.SELECT && (
                <SelectFilter value={filterValue} options={filterOptions ?? []} setValue={handleUpdateColumnFilter} />
              )}
              {filterType === FilterType.SELECT_YES_NO && (
                <SelectFilter
                  value={filterValue}
                  setValue={handleUpdateColumnFilter}
                  options={[
                    { label: 'Yes', value: 'yes' },
                    { label: 'No', value: 'no' },
                  ]}
                />
              )}
              {filterType === FilterType.MULTI_SELECT && (
                <UncontrolledMultiSelect
                  value={filterValue}
                  options={filterOptions ?? []}
                  onChange={handleUpdateColumnFilter}
                />
              )}
              {filterType === FilterType.DATE && (
                <DateFilter value={filterValue} direction={'row'} setValue={handleUpdateColumnFilter} />
              )}
              {filterType === FilterType.RANGE && (
                <RangeFilter value={filterValue} direction={'row'} setValue={handleUpdateColumnFilter} />
              )}
            </>
          )}
        </Flex>
      </Flex>
    )
  }
)
export default TableProfileColumnItem
